import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { todayIso } from "@/lib/entries";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Users, User } from "lucide-react";

function computeCurrentLine(text, caret) {
  const before = text.slice(0, caret);
  const lineStart = before.lastIndexOf("\n") + 1;
  const afterNewline = text.indexOf("\n", caret);
  const lineEnd = afterNewline === -1 ? text.length : afterNewline;
  return { lineStart, lineEnd, value: text.slice(lineStart, lineEnd) };
}

export default function BulkEntryDialog({ open, onOpenChange, onSaved }) {
  const [names, setNames] = useState("");
  const [entryDate, setEntryDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [caret, setCaret] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [active, setActive] = useState(0);
  const textareaRef = useRef(null);
  const suppressRef = useRef(false);

  useEffect(() => {
    if (open) {
      setNames(""); setEntryDate(todayIso());
      setSuggestions([]); setOpenList(false); setCaret(0);
    }
  }, [open]);

  const currentLine = computeCurrentLine(names, caret).value.trim();

  useEffect(() => {
    if (suppressRef.current) { suppressRef.current = false; return; }
    if (!currentLine || currentLine.length < 2) {
      setSuggestions([]); setOpenList(false); return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients/suggest", { params: { q: currentLine } });
        // Exclude names already listed above the caret so duplicates aren't offered.
        const already = new Set(
          names.split("\n").map((n) => n.trim().toLowerCase()).filter(Boolean)
        );
        const list = (data.names || []).filter(
          (n) => n.toLowerCase() !== currentLine.toLowerCase() && !already.has(n.toLowerCase())
        );
        setSuggestions(list);
        setActive(0);
        setOpenList(list.length > 0);
      } catch (err) { console.error("Patient suggestions failed:", err); }
    }, 180);
    return () => clearTimeout(t);
  }, [currentLine, names]);

  const pick = (name) => {
    const { lineStart, lineEnd } = computeCurrentLine(names, caret);
    const replaced = names.slice(0, lineStart) + name + names.slice(lineEnd);
    // Move caret to end of the replaced name, then add a newline so the next line is ready.
    const nextCaret = lineStart + name.length;
    const withNewline = replaced.slice(0, nextCaret) + "\n" + replaced.slice(nextCaret);
    suppressRef.current = true;
    setNames(withNewline);
    setOpenList(false);
    setSuggestions([]);
    // Restore focus + caret position after state update
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const target = nextCaret + 1;
        el.setSelectionRange(target, target);
        setCaret(target);
      }
    });
  };

  const onKeyDown = (e) => {
    if (openList && suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" && suggestions[active]) { e.preventDefault(); pick(suggestions[active]); return; }
      if (e.key === "Tab" && suggestions[active])   { e.preventDefault(); pick(suggestions[active]); return; }
      if (e.key === "Escape") { setOpenList(false); return; }
    }
  };

  const parsed = names.split("\n").map((n) => n.trim()).filter(Boolean);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (parsed.length === 0) {
      toast.error("Please add at least one patient name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/entries/bulk", { names: parsed, entry_date: entryDate });
      toast.success(`Added ${data.count} ${data.count === 1 ? "entry" : "entries"}`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users size={18} className="text-sky-600" /> Add multiple patients
          </DialogTitle>
          <DialogDescription>
            One name per line. Type 2+ letters to see suggestions from existing patients — press <kbd className="px-1 py-0.5 rounded border text-[10px]">Enter</kbd> or <kbd className="px-1 py-0.5 rounded border text-[10px]">Tab</kbd> to pick.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={entryDate}
                   onChange={(e) => setEntryDate(e.target.value)}
                   data-testid="bulk-entry-date-input" />
          </div>

          <div className="space-y-2">
            <Label>Patient names (one per line)</Label>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                rows={8}
                value={names}
                onChange={(e) => { setNames(e.target.value); setCaret(e.target.selectionStart); }}
                onClick={(e) => setCaret(e.target.selectionStart)}
                onKeyUp={(e) => setCaret(e.target.selectionStart)}
                onKeyDown={onKeyDown}
                onBlur={() => setTimeout(() => setOpenList(false), 120)}
                placeholder="Rahul Sharma&#10;Priya Patel&#10;Amit Singh"
                data-testid="bulk-entry-names-input"
              />
              {openList && suggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md"
                     data-testid="bulk-name-suggestions">
                  {suggestions.map((n, i) => (
                    <button key={n} type="button"
                            onMouseDown={(e) => { e.preventDefault(); pick(n); }}
                            onMouseEnter={() => setActive(i)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === active ? "bg-sky-50 text-sky-700" : "text-slate-700 hover:bg-slate-50"}`}
                            data-testid={`bulk-name-suggestion-${i}`}>
                      <User size={14} className="text-slate-400" /> {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500" data-testid="bulk-entry-count">
              {parsed.length} {parsed.length === 1 ? "name" : "names"} ready to add
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline"
                    onClick={() => onOpenChange(false)}
                    data-testid="bulk-entry-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving || parsed.length === 0}
                    className="bg-sky-500 hover:bg-sky-600 text-white"
                    data-testid="bulk-entry-save">
              {saving ? "Saving..." : `Add ${parsed.length || ""} ${parsed.length === 1 ? "entry" : "entries"}`.trim()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
